import { useEffect, useRef, useState } from 'react';

/**
 * Trade ideas are an optional overlay on the actual-portfolio holdings model.
 * Load them only when Optimizer is displaying the actual/current baseline;
 * Position Sizing owns a separate on-demand data workflow.
 */
export default function useOnDemandActualTradeIdeas({
    userUid,
    activeView,
    optimizerViewMode,
    startupReady,
    enabled = true,
    contextKey,
    fetchTradeIdeas,
    setActualPortTradeIdeas,
}) {
    const currentContextKeyRef = useRef(contextKey);
    const requestedContextKeyRef = useRef('');
    const [resolvedContextKey, setResolvedContextKey] = useState('');
    currentContextKeyRef.current = contextKey;

    useEffect(() => {
        if (enabled && userUid && contextKey) return;
        requestedContextKeyRef.current = '';
        setResolvedContextKey('');
        setActualPortTradeIdeas(enabled ? null : []);
    }, [contextKey, enabled, setActualPortTradeIdeas, userUid]);

    useEffect(() => {
        if (!enabled || !userUid || !startupReady || !contextKey) return;
        if (activeView !== 'optimizer' || optimizerViewMode !== 'normal') return;
        if (requestedContextKeyRef.current === contextKey) return;

        requestedContextKeyRef.current = contextKey;
        setActualPortTradeIdeas(null);
        const request = fetchTradeIdeas(null, {
            isCurrentRequest: () => currentContextKeyRef.current === contextKey,
        });
        const markResolved = () => {
            if (currentContextKeyRef.current === contextKey) {
                setResolvedContextKey(contextKey);
            }
        };
        void Promise.resolve(request).then(markResolved, markResolved);
    }, [
        activeView,
        contextKey,
        enabled,
        fetchTradeIdeas,
        optimizerViewMode,
        setActualPortTradeIdeas,
        startupReady,
        userUid,
    ]);

    return {
        loading: Boolean(
            userUid
            && enabled
            && startupReady
            && contextKey
            && activeView === 'optimizer'
            && optimizerViewMode === 'normal'
            && resolvedContextKey !== contextKey
        ),
    };
}
